import localStorage from './local-storage';
import cookie from './cookie';

import urlUtils from '../util/url';
import store from '../state/store';
import { setToken, logout } from '../state/modules/auth';
import { stopLoading } from '../state/modules/utils';

export const API_HOST = process.env.REACT_APP_API_HOST;
export const API_ROOT = process.env.REACT_APP_API_ROOT;

export const http = (() => {

  // Single-flight refresh: while a refresh is in progress every caller awaits the
  // same promise, so a burst of expired requests triggers exactly one refresh.
  let refreshPromise = null;

  function authHeaders() {
    return { 'Authorization': 'Bearer ' + localStorage.get('ACCESS_TOKEN') };
  }

  // Refreshes the access token using the stored refresh token. Uses a raw fetch
  // (not http.request) so it can never recurse back into the refresh-and-retry logic.
  function refreshAccessToken() {

    if (! refreshPromise) {

      let body = new FormData();
      body.append('refresh_token', JSON.stringify(cookie.get('refreshToken')));

      refreshPromise = fetch(API_HOST + API_ROOT + 'refresh-token', { method: 'POST', body: body })
        .then(response => response.json())
        .then(json => {
          if (! json.success) {
            throw new Error(json.error_code || 'refresh_failed');
          }

          // The backend rotates the refresh token on every refresh, so persist both.
          store.dispatch(setToken(json.result.access_token, json.result.refresh_token));

          return json.result.access_token;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    return refreshPromise;
  }

  // Sends the request and, when the access token has expired, transparently refreshes
  // it and replays the request once. The caller's promise resolves with real data and
  // never sees the expiry, so no unhandled rejection and no leftover loading state.
  function send(config, authRequired, allowRefresh) {

    if (authRequired) {
      config.headers = { ...(config.headers || {}), ...authHeaders() };
    }

    return fetch(config.url, config)
      .then(response => response.json())
      .then(json => {

        if (json.success) {
          return json;
        }

        if (json.error_code === 'expired_jwt' && authRequired && allowRefresh) {
          return refreshAccessToken()
            .then(() => send(config, authRequired, false))
            .catch(error => {
              // The refresh token itself is dead (e.g. expired): log out cleanly and
              // clear the loading state so the user lands on the login screen, not a
              // frozen spinner.
              store.dispatch(logout());
              store.dispatch(stopLoading());
              throw error;
            });
        }

        throw new Error(json.error_code);
      });
  }

  return {

    request(config = {}, authRequired = false) {
      return send(config, authRequired, true);
    },

    get(url, authRequired = false, params = {}) {
      return this.request({
        url: urlUtils.urlWithQuery(url, params)
      }, authRequired);
    },

    post(url, data, authRequired = false) {

      let formData = new FormData();

      for (let prop in data) {
        if (data.hasOwnProperty(prop)) {
          formData.append(prop, JSON.stringify(data[prop]));
        }
      }

      return this.request({
        url: url,
        method: 'POST',
        body: formData
      }, authRequired);
    },

    put(url, data, authRequired = false) {
      return this.request({
        url: url,
        method: 'PUT',
        body: JSON.stringify(data)
      }, authRequired);
    },

    patch(url, data, authRequired = false) {
      return this.request({
        url: url,
        method: 'PATCH',
        body: JSON.stringify(data)
      }, authRequired);
    },

    delete(url, authRequired = false) {
      return this.request({
        url: url,
        method: 'DELETE',
      }, authRequired);
    }
  }
})();
